import api from './api';
import type { ExcelExportJobResponse } from './excel.service';

/**
 * TCR-1954 — popup Tra cứu tồn kho mở từ icon trên header.
 *
 * Bốn chế độ dùng bốn endpoint riêng vì SHAPE TRẢ VỀ khác nhau hẳn (chế độ lô/mã quy cách
 * có dòng theo từng kho, chế độ hàng hoá/combo gộp toàn kho). Đừng gộp thành một endpoint
 * nhận `mode` rồi narrow type ở FE.
 */

export type StockLookupMode = 'products' | 'lots' | 'serials' | 'combos';

export interface StockLookupQuery {
  page?: number;
  page_size?: number;
  search?: string;
  warehouse_id?: string;
  store_id?: string;
  category_id?: string;
}

/** TCR-1988 — một hàng hoá có thể thuộc nhiều danh mục. */
export interface StockLookupCategory {
  id: string;
  name: string;
}

export interface StockLookupProductRow {
  variant_id: string;
  sku: string;
  variant_code: string;
  product_name: string;
  category: string;
  /** TCR-1988 — MỌI danh mục (`category` chỉ là phần tử đầu). BE cũ chưa trả thì vắng. */
  categories?: StockLookupCategory[];
  uom: string;
  unit_in_stock: number;
  unit_reserved: number;
  available_quantity: number;
  /** Not currently provided by the product-level inventory read model. */
  packaging_content?: string;
  /**
   * TCR-2523 — tồn bên AMIS/Misa của mặt hàng này.
   *
   * BE cũ (chưa có phần đồng bộ tồn AMIS) KHÔNG trả field này → `undefined`, FE phải
   * chịu được cả hai trạng thái: có số (đối chiếu với ERP) và không có số (hiện "—",
   * KHÔNG hiển thị 0 vì 0 trông như "hết hàng").
   */
  amis_quantity?: number | null;
  /**
   * TCR-2649 — tồn theo từng kho (chỉ kho có tồn ≠ 0, kho nhiều tồn trước) để cột "Số lượng"
   * hiện luôn `MÃ KHO: SL`. BE cũ chưa trả thì vắng ⇒ chỉ hiện con số tổng như trước.
   */
  warehouses?: StockLookupProductWarehouse[];
}

/** TCR-2649 — một kho trong cột "Số lượng" của tra cứu tồn kho. */
export interface StockLookupProductWarehouse {
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  store_name: string;
  quantity: number;
}

export interface StockLookupLotRow {
  lot_id: string;
  warehouse_name: string;
  sku: string;
  product_name: string;
  category: string;
  uom: string;
  lot_number: string;
  expiry_date: string | null;
  receipt_date?: string | null;
  status?: 'available' | 'reserved' | 'depleted' | 'expired' | 'blocked' | 'quarantine' | string;
  quantity: number;
  locations?: Array<{
    location_code: string;
    quantity: number;
    reserved_quantity: number;
    available_quantity: number;
  }>;
}

export interface StockLookupSerialRow {
  serial_id: string;
  warehouse_name: string;
  sku: string;
  product_name: string;
  category: string;
  uom: string;
  serial_number: string;
  quantity: number;
}

export interface StockLookupComboRow {
  variant_id: string;
  sku: string;
  product_name: string;
  category: string;
  categories?: StockLookupCategory[];
  uom: string;
  unit_in_stock: number;
}

export interface WarehouseBreakdownRow {
  /** TCR-2573 — BE trước bản này không trả 3 field chi nhánh. */
  store_id?: string;
  store_code?: string;
  store_name?: string;
  warehouse_id: string;
  /** TCR-2649 — BE cũ chưa trả thì vắng ⇒ cột Kho chỉ hiện tên. */
  warehouse_code?: string;
  warehouse_name: string;
  quantity: number;
}

export interface ReservedOrderRow {
  order_id: string;
  order_number: string;
  order_date: string;
  customer_name: string;
  quantity: number;
  /** Nguồn giữ hàng (thiết kế §4.6): đơn hàng thường hoặc đơn sàn (TCR-1990). */
  source_type: 'order' | 'oc_order';
  /** Chỉ đơn sàn: kênh (`TIKTOK`, `SHOPEE`...). */
  channel?: string;
  /** Chỉ đơn sàn: trạng thái đơn (`COMPLETED`, `CANCELLED`...). */
  state?: string;
  /** TCR-1997 — người lên đơn (`created_by`); đơn sàn luôn `null`. */
  created_by_name?: string | null;
  created_by_staff_code?: string | null;
  /**
   * TCR-1997 — `false` = đơn ngoài phạm vi xem của người dùng: vẫn liệt kê để biết ai đang giữ
   * hàng, nhưng KHÔNG mở chi tiết. BE cũ không trả field này → coi như xem được.
   */
  can_view?: boolean;
}

export interface ReservedOrdersResponse {
  data: ReservedOrderRow[];
  meta: {
    /**
     * Tổng giữ hàng theo reservation TRONG PHẠM VI CHI NHÁNH của người đang xem, kể cả
     * phần không liệt kê được thành đơn. KHÔNG còn là tổng toàn tenant (fix round 2).
     */
    reserved_total: number;
    /** Tổng của các đơn thực sự hiện trong danh sách. */
    listed_total: number;
    /** Số đơn khớp trong phạm vi xem của người dùng (trước khi cắt trang). */
    matched_order_count: number;
    /** Số đơn thực sự trả về. */
    listed_order_count: number;
    /** BE cắt danh sách đơn ở 100 — bật khi còn đơn chưa liệt kê. */
    order_list_truncated: boolean;
    /** BE cắt số reservation quét ở 500 — bật khi mặt hàng có quá nhiều phiếu giữ. */
    reservation_scan_truncated: boolean;
    /** Có phần giữ hàng chưa gắn kho nên không quy được về chi nhánh, đã bị loại khỏi tổng. */
    unattributed_excluded: boolean;
    /** Phần giữ hàng của nguồn KHÔNG resolve được thành đơn (chuyển kho, giữ tay, hoá đơn). */
    non_order_reserved_total: number;
    /** TCR-1990 — `non_order_reserved_total` tách theo `source_type`. */
    non_order_reserved_by_type?: Record<string, number>;
    /** TCR-1990 — phần giữ của đơn hàng không liệt kê được (ngoài phạm vi xem / bị cắt). */
    order_hidden_total?: number;
    /** TCR-1990 — phần giữ của đơn sàn không liệt kê được (thiếu quyền xem đơn sàn). */
    ecom_order_hidden_total?: number;
  };
}

/**
 * Envelope CHUNG của cả 4 chế độ: `{ data, total }`.
 *
 * Chế độ lô đi qua `InventoryLotService.list()` vốn trả `{ data, meta: { total } }`;
 * facade BE chuẩn hoá lại về đúng envelope này (fix round 2). Trước đó `data?.total` luôn
 * là `undefined` nên bảng lô báo "0 dòng" và antd không render nút sang trang.
 */
export interface StockLookupListResponse<TRow> {
  data: TRow[];
  total: number;
  /**
   * TCR-2523 — thời điểm gần nhất tồn AMIS được kéo về (ISO).
   *
   * Cấp RESPONSE (không phải cấp dòng) vì đây là mốc thời gian của cả lần kéo dữ
   * liệu, không phải của từng mặt hàng. BE cũ không trả → vắng, FE không hiện chú thích.
   */
  amis_as_of?: string | null;
}

/**
 * BE bật `forbidNonWhitelisted`, và param rỗng/undefined lọt qua axios sẽ thành `?search=`
 * làm request trả 400. Lọc sạch trước khi gửi.
 */
function cleanParams<T extends object>(query: T): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(query) as [string, unknown][]) {
    if (value === undefined || value === null || value === '') continue;
    params[key] = value;
  }
  return params;
}

export const stockLookupService = {
  products: (query: StockLookupQuery) =>
    api
      .get<StockLookupListResponse<StockLookupProductRow>>(
        '/inventory/stock-lookup/products',
        { params: cleanParams(query) },
      )
      .then((r) => r.data),

  lots: (query: StockLookupQuery) =>
    api
      .get<StockLookupListResponse<StockLookupLotRow>>(
        '/inventory/stock-lookup/lots',
        { params: cleanParams(query) },
      )
      .then((r) => r.data),

  serials: (query: StockLookupQuery) =>
    api
      .get<StockLookupListResponse<StockLookupSerialRow>>(
        '/inventory/stock-lookup/serials',
        { params: cleanParams(query) },
      )
      .then((r) => r.data),

  combos: (query: StockLookupQuery) =>
    api
      .get<StockLookupListResponse<StockLookupComboRow>>(
        '/inventory/stock-lookup/combos',
        { params: cleanParams(query) },
      )
      .then((r) => r.data),

  warehouseBreakdown: (params: { variant_id: string; warehouse_id?: string; store_id?: string }) =>
    api
      .get<{ data: WarehouseBreakdownRow[]; total: number }>(
        '/inventory/stock-lookup/warehouse-breakdown',
        { params: cleanParams(params) },
      )
      .then((r) => r.data),

  reservedOrders: (params: { variant_id: string; warehouse_id?: string; store_id?: string }) =>
    api
      .get<ReservedOrdersResponse>('/inventory/stock-lookup/reserved-orders', {
        params: cleanParams(params),
      })
      .then((r) => r.data),

  requestExport: (mode: StockLookupMode, query: StockLookupQuery) =>
    api
      // BE trả về bản serialize của job export (`data-export.service.ts`): `export_id` +
      // `status` + metadata, KHÔNG có `job_id`. Cùng shape với job của `/excel/export-jobs`
      // nên caller đưa thẳng vào `waitForExportJobDownload()` để poll và tải file (TCR-1986).
      .get<ExcelExportJobResponse>('/inventory/stock-lookup/export', {
        params: cleanParams({ ...query, mode }),
      })
      .then((r) => r.data),
};
